/**
 * BankTransaction classification side panel — the canonical entry
 * point for classifying any bank line (Contribution, Expenditure, Exclude
 * from report). Mounted from the staged-page detail surface and,
 * post-USI-007, from the unified sort-triage inbox.
 *
 * ADR-014 / EFR-002: the retired Transfer / Pair / Dismiss off-ramps
 * collapsed into one "Exclude from report" resolution (`ExcludedFromReport`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  updateFinanceContactRequest,
  ReceiptNature,
  ElectionDesignation,
  ExpenditureClassificationSubCategory,
  PaymentMethod,
  FINANCE_MAX_BANK_TRANSACTION_EXCLUDE_NOTE_LENGTH,
  getGates,
  requiresInKindNature,
  normalizeTransferQualification,
} from '@signalvote/shared';
import type { BankTransactionClassificationHintDto, CreateFinanceContactRequest, RuleOutcomeDto } from '@signalvote/shared';
import {
  PanelHeader,
  TypeToggle,
  ActionFooter,
  type TypeToggleOption,
  type ClassifyHint,
  type ContactAddressPatch,
} from '@/components/finance/shared/classification';
import { useCreateFinanceContact, useUpdateFinanceContact } from '@/hooks/queries/useFinanceContacts';
import { useDonorPickerContacts } from '@/hooks/queries/useDonorPickerContacts';
import { useFinanceSettings, useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useValidateDraftContribution, useFinanceContributions } from '@/hooks/queries/useFinanceContributions';
import {
  useBankTransactionClassificationForm,
  type BankTransactionClassificationType,
  type BankTransactionContributionPrefill,
} from './useBankTransactionClassificationForm';
import {
  BankTransactionClassifyError,
  BankTransactionConcurrencyConflictError,
  useClassifyBankTransactionAsContribution,
  useExcludeBankTransactionFromReport,
  useClassifyBankTransactionAsExpenditure,
  useReceiptSuggestions,
} from '@/hooks/queries/useBankTransactionClassification';
import { classificationHintReference } from '@/lib/finance/classificationHint';
import { ContributionPathBody } from './ContributionPathBody';
import { ExpenditurePathBody } from './ExpenditurePathBody';

const BASE_TYPE_OPTIONS: ReadonlyArray<TypeToggleOption<BankTransactionClassificationType>> = [
  { value: 'contribution', label: 'Contribution' },
  { value: 'expenditure', label: 'Expenditure' },
];

// ADR-014 — the single non-reporting off-ramp, always LAST (Exclude is the
// muted secondary resolution; the money paths lead).
const EXCLUDE_OPTION: TypeToggleOption<BankTransactionClassificationType> = {
  value: 'exclude',
  label: 'Exclude from report',
};

export interface BankTransactionPanelRow {
  id: string;
  date: string;
  amount: number;
  description: string;
  sourceLabel: string;
  concurrencyVersion: number;
  /** BSW-008: BSW-005 suggestions from the reconciliation endpoint. */
  suggestedContactId?: string | null;
  suggestedPaymentMethod?: PaymentMethod | null;
  suggestedReferenceNumber?: string | null;
}

interface BankTransactionClassificationPanelProps {
  row: BankTransactionPanelRow;
  onClose: () => void;
  /**
   * Fired after a successful classify/mutation. Receives the id of the entity
   * the classify created (the new Contribution / Expenditure), when one was
   * created, so the ledger can carry the flow straight into that row's editor
   * if it still needs work instead of collapsing and forcing a re-click. Undone
   * for the non-creating mutation (exclude from report).
   */
  onClassified?: (newEntityId?: string) => void;
  /** USI-008: see `UseFormArgs.initialSelectedType`. */
  initialSelectedType?: BankTransactionClassificationType | null;
  /**
   * USI-008: when true, suppress the TypeToggle UI. Used by the triage
   * modal where the outer tile picker is the type-selection affordance.
   */
  hideTypeToggle?: boolean;
  /** The bank line's classification hint — pre-fills the Expenditure path. See
   *  `UseFormArgs.expenditureHint`. */
  classificationHint?: BankTransactionClassificationHintDto | null;
}

const VALIDATE_DEBOUNCE_MS = 300;

export function BankTransactionClassificationPanel({
  row,
  onClose,
  onClassified,
  initialSelectedType,
  hideTypeToggle,
  classificationHint,
}: BankTransactionClassificationPanelProps) {
  const prefill = useMemo<BankTransactionContributionPrefill>(
    () => ({
      contactId: row.suggestedContactId ?? null,
      paymentMethod: row.suggestedPaymentMethod ?? null,
      referenceNumber: row.suggestedReferenceNumber ?? null,
    }),
    [row.suggestedContactId, row.suggestedPaymentMethod, row.suggestedReferenceNumber],
  );

  // AL in-kind requires a chosen nature before Classify unlocks. The jurisdiction
  // rule is the shared `requiresInKindNature` predicate (the same one the close-ledger
  // in-kind capture gates on) so neither surface re-derives "which states need a
  // nature". Read the profile before the form hook so the gate is wired at creation.
  const { data: profile } = useComplianceProfile();

  const form = useBankTransactionClassificationForm({
    contributionPrefill: prefill,
    initialSelectedType,
    requireInKindNature: requiresInKindNature(profile?.stateCode),
    expenditureHint: classificationHint,
  });
  const {
    selectedType,
    setSelectedType,
    canClassify,
    submission,
    setSubmission,
    reset,
    contactId,
    contributionType,
    electionDesignation,
    electionYear,
    paymentMethod,
    referenceNumber,
    notes,
    excludeNote,
    setExcludeNote,
    inKindFairMarketValueBasis,
    inKindNature,
    transferQualification,
    overriddenRuleCodes,
    addOverride,
    cancelQuickCreate,
    setContactId,
    expenditureSubCategory,
    expenditureContactId,
    expenditureCategory,
    expenditurePurpose,
    expenditureSettlesDebtId,
    expenditureRepaysLoanId,
    expenditureRefundOfContributionId,
    cancelExpenditureQuickCreate,
    setExpenditureContactId,
  } = form;

  // Donors who have given (plus every payee), falling through to prospects on a search that
  // matches no one — the payee picker filters to payees, so it never sees a prospect.
  const {
    contacts,
    prospects,
    onSearchChange: onContactSearchChange,
  } = useDonorPickerContacts(contactId);
  // BSW-009: Refund sub-category needs the campaign's confirmed contributions
  // to pick the original. Pulled lazily by the same hook the existing
  // refund flow uses; pageSize 100 mirrors the contact picker shape.
  const { data: refundCandidatesData } = useFinanceContributions({ pageSize: 100 });
  const refundCandidates = refundCandidatesData?.items ?? [];
  const { data: settings } = useFinanceSettings();
  const { mutateAsync: createContactAsync, isPending: isCreatingContact } = useCreateFinanceContact();
  const { mutateAsync: updateContactAsync, isPending: isSavingContactAddress } = useUpdateFinanceContact();
  const { mutateAsync: validateAsync } = useValidateDraftContribution();
  const { mutateAsync: classifyAsContributionAsync, isPending: isClassifyingContribution } =
    useClassifyBankTransactionAsContribution();
  const { mutateAsync: classifyAsExpenditureAsync, isPending: isClassifyingExpenditure } =
    useClassifyBankTransactionAsExpenditure();
  const { mutateAsync: excludeFromReportAsync, isPending: isExcluding } =
    useExcludeBankTransactionFromReport();

  // Every row — credit or debit — offers the same three paths (Contribution,
  // Expenditure, Exclude). Exclude is always LAST (ADR-014): money paths lead,
  // the muted off-ramp trails. (The credit-only "Deposit of recorded checks"
  // option was retired 2026-07-14 — check deposits now resolve via Exclude.)
  const typeOptions = useMemo(
    () => [...BASE_TYPE_OPTIONS, EXCLUDE_OPTION],
    [],
  );

  const [gateOutcomes, setGateOutcomes] = useState<RuleOutcomeDto[]>([]);

  // CAP-006 — pocket-receipt suggest-match. Only queried while the Expenditure
  // path is selected on a debit row; the treasurer opts a suggestion in (we
  // never auto-attach). The Refund sub-category rejects a receipt server-side
  // (a refund creates no Expenditure to attach to), so we drop the selection
  // when the sub-category is Refund and never send the id on that branch.
  const [selectedCapturedReceiptId, setSelectedCapturedReceiptId] = useState<string | null>(null);
  const { data: receiptSuggestionsData } = useReceiptSuggestions(
    row.id,
    selectedType === 'expenditure',
  );
  const receiptSuggestions = receiptSuggestionsData ?? [];
  const receiptAttachable =
    expenditureSubCategory !== ExpenditureClassificationSubCategory.Refund;
  // A selection stranded by a switch to Refund must not leak into the payload.
  useEffect(() => {
    if (!receiptAttachable && selectedCapturedReceiptId !== null) {
      setSelectedCapturedReceiptId(null);
    }
  }, [receiptAttachable, selectedCapturedReceiptId]);

  // BSW-008: debounced validate-draft (CRE-009 convention). Fires only on
  // the Contribution path with a picked contact; clears outcomes when
  // the path switches.
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (selectedType !== 'contribution' || !contactId) {
      setGateOutcomes([]);
      return;
    }
    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    validateTimerRef.current = setTimeout(async () => {
      try {
        const result = await validateAsync({
          amount: row.amount,
          date: row.date,
          contributionType,
          electionDesignation,
          contactId,
          paymentMethod: paymentMethod ?? null,
          // #711 — so the live gate judges the same answer a classify would store.
          transferQualification: normalizeTransferQualification(contributionType, transferQualification),
        });
        setGateOutcomes(getGates(result.outcomes));
      } catch {
        // Non-fatal — gates will be re-checked at classify time
      }
    }, VALIDATE_DEBOUNCE_MS);
    return () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    };
  }, [
    selectedType,
    contactId,
    contributionType,
    electionDesignation,
    paymentMethod,
    row.amount,
    row.date,
    validateAsync,
  ]);

  const blockingGates = useMemo(
    () => gateOutcomes.filter((g) => !overriddenRuleCodes.has(g.ruleCode)),
    [gateOutcomes, overriddenRuleCodes],
  );
  const blockedByGates = blockingGates.length > 0;

  const handleAfterMutation = useCallback(
    (newEntityId?: string) => {
      reset();
      setGateOutcomes([]);
      setSelectedCapturedReceiptId(null);
      if (onClassified) {
        onClassified(newEntityId);
      } else {
        onClose();
      }
    },
    [reset, onClassified, onClose],
  );

  const handleExcludeFromReport = useCallback(async () => {
    setSubmission({ kind: 'submitting' });
    try {
      await excludeFromReportAsync({
        row,
        // Verbatim when content-bearing (the server stores it as-is and
        // omits it from the audit JSON when blank); null for whitespace-only.
        note: excludeNote.trim() === '' ? null : excludeNote,
      });
      handleAfterMutation();
    } catch (err) {
      if (err instanceof BankTransactionConcurrencyConflictError) {
        setSubmission({
          kind: 'error',
          message: 'This row was modified — refreshing.',
        });
        return;
      }
      // A server NoteTooLong (or any coded 4xx) surfaces its message verbatim.
      if (err instanceof BankTransactionClassifyError) {
        setSubmission({ kind: 'error', message: err.message });
        return;
      }
      setSubmission({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to exclude from report',
      });
    }
  }, [
    excludeFromReportAsync,
    excludeNote,
    handleAfterMutation,
    row.concurrencyVersion,
    row.id,
    setSubmission,
  ]);

  // Bank-fee-line steering (spec §D7): the one-gesture "this bank line is the
  // already-booked fee" action. Excludes the raw line from the report (ADR-014's
  // single non-reporting resolution) with the prefilled note instead of booking a
  // duplicate fee expenditure. Reuses the same exclude mutation as the panel's
  // Exclude path; the note is supplied by the fee-check warning, not the textarea.
  const handleExcludeWithNote = useCallback(
    async (note: string) => {
      await excludeFromReportAsync({
        row,
        note,
      });
    },
    [excludeFromReportAsync, row.id, row.concurrencyVersion],
  );

  const handleQuickCreateSave = useCallback(async (request: CreateFinanceContactRequest) => {
    try {
      const created = await createContactAsync(request);
      setContactId(created.contact.id);
      cancelQuickCreate();
    } catch (err) {
      setSubmission({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to create contact',
      });
    }
  }, [
    cancelQuickCreate,
    createContactAsync,
    setContactId,
    setSubmission,
  ]);

  const handleClassifyContribution = useCallback(async () => {
    setSubmission({ kind: 'submitting' });
    try {
      const created = await classifyAsContributionAsync({
        id: row.id,
        data: {
          bankTransactionConcurrencyVersion: row.concurrencyVersion,
          contactId,
          contributionType,
          electionDesignation,
          // #144 — carry the off-cycle year (null for own cycle); server normalizes it on Undesignated.
          electionYear,
          paymentMethod: paymentMethod ?? null,
          notes: composeNotes(notes, referenceNumber),
          inKindFairMarketValueBasis:
            contributionType === ReceiptNature.InKind
              ? inKindFairMarketValueBasis
              : null,
          inKindNature:
            contributionType === ReceiptNature.InKind ? inKindNature : null,
          // #711 — the recorded qualifying relationship; null on every other receipt, where the
          // question decides nothing. The shared normalizer mirrors the server's.
          transferQualification: normalizeTransferQualification(contributionType, transferQualification),
        },
      });
      // Carry the new contribution's id forward so the ledger can open it if it
      // still needs donor info (itemized) instead of collapsing.
      handleAfterMutation(created.id);
    } catch (err) {
      if (err instanceof BankTransactionConcurrencyConflictError) {
        setSubmission({
          kind: 'error',
          message: 'This row was modified — refreshing.',
        });
        return;
      }
      if (err instanceof BankTransactionClassifyError) {
        setSubmission({ kind: 'error', message: err.message });
        return;
      }
      setSubmission({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to classify',
      });
    }
  }, [
    classifyAsContributionAsync,
    contactId,
    contributionType,
    electionDesignation,
    electionYear,
    handleAfterMutation,
    inKindFairMarketValueBasis,
    inKindNature,
    notes,
    paymentMethod,
    referenceNumber,
    row.concurrencyVersion,
    row.id,
    setSubmission,
  ]);

  const handleExpenditureQuickCreateSave = useCallback(async (request: CreateFinanceContactRequest) => {
    try {
      const created = await createContactAsync(request);
      setExpenditureContactId(created.contact.id);
      cancelExpenditureQuickCreate();
    } catch (err) {
      setSubmission({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to create payee',
      });
    }
  }, [
    cancelExpenditureQuickCreate,
    createContactAsync,
    setExpenditureContactId,
    setSubmission,
  ]);


  // Fill in a missing mailing address on the ALREADY-selected contact without
  // leaving the panel. Mutates the shared FinanceContact (correct for an
  // address fix — it should apply everywhere), then the contacts query
  // invalidates so the picker reflects the completed address.
  const handleSaveContactAddress = useCallback(
    async (
      contactId: string,
      concurrencyVersion: number,
      patch: ContactAddressPatch,
    ) => {
      try {
        await updateContactAsync({
          id: contactId,
          data: updateFinanceContactRequest({
            address: patch.address.trim() || null,
            addressLine2: patch.addressLine2.trim() || null,
            city: patch.city.trim() || null,
            state: patch.state.trim() || null,
            zipCode: patch.zip.trim() || null,
            concurrencyVersion,
          }),
        });
      } catch (err) {
        setSubmission({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to save address',
        });
      }
    },
    [updateContactAsync, setSubmission],
  );

  const handleClassifyExpenditure = useCallback(async () => {
    if (expenditureSubCategory === null) return;
    setSubmission({ kind: 'submitting' });
    const isGeneral =
      expenditureSubCategory === ExpenditureClassificationSubCategory.GeneralExpenditure;
    // The form has no Maryland picker, so the hint's coded pair rides along only while the
    // category is still the hint's — a different category may need a different coded pair.
    const keepsHintMdPair =
      isGeneral && classificationHint != null && expenditureCategory === classificationHint.category;
    try {
      const created = await classifyAsExpenditureAsync({
        id: row.id,
        data: {
          bankTransactionConcurrencyVersion: row.concurrencyVersion,
          subCategory: expenditureSubCategory,
          contactId:
            expenditureSubCategory === ExpenditureClassificationSubCategory.GeneralExpenditure
              ? expenditureContactId
              : null,
          category:
            expenditureSubCategory === ExpenditureClassificationSubCategory.GeneralExpenditure
              ? expenditureCategory
              : null,
          purpose:
            expenditureSubCategory === ExpenditureClassificationSubCategory.GeneralExpenditure
              ? expenditurePurpose.trim()
              : null,
          refundOfContributionId:
            expenditureSubCategory === ExpenditureClassificationSubCategory.Refund
              ? expenditureRefundOfContributionId
              : null,
          // BSW-009 v1: no explicit processor picker; server fuzzy-matches.
          paymentProcessorId: null,
          // CAP-006 — the picked pocket receipt (null when none / on the Refund
          // branch, which has no Expenditure to attach to and rejects it 400).
          capturedReceiptId: receiptAttachable ? selectedCapturedReceiptId : null,
          // ADR-031 — the debt this settles (GeneralExpenditure branch only; the fee/refund
          // sub-categories are never debt payments). Guarded server-side against overpayment.
          debtId:
            expenditureSubCategory === ExpenditureClassificationSubCategory.GeneralExpenditure
              ? expenditureSettlesDebtId
              : null,
          // The loan this repays — same GeneralExpenditure gating as the debt link (a processor
          // fee or a refund is never a loan repayment). Guarded server-side against repaying
          // more than the principal.
          loanId:
            expenditureSubCategory === ExpenditureClassificationSubCategory.GeneralExpenditure
              ? expenditureRepaysLoanId
              : null,
          mdExpenseCategory: keepsHintMdPair ? classificationHint.mdExpenseCategory : null,
          mdExpensePurpose: keepsHintMdPair ? classificationHint.mdExpensePurpose : null,
          // The hint the form was pre-filled from, so the server records whether it was kept
          // as-is or edited.
          hint: classificationHint ? classificationHintReference(classificationHint) : null,
        },
      });
      // Carry the new entity's id forward so the ledger can open it if it still
      // needs work (an over-limit payee address, etc.) instead of collapsing.
      handleAfterMutation(created.id);
    } catch (err) {
      if (err instanceof BankTransactionConcurrencyConflictError) {
        setSubmission({
          kind: 'error',
          message: 'This row was modified — refreshing.',
        });
        return;
      }
      if (err instanceof BankTransactionClassifyError) {
        setSubmission({ kind: 'error', message: err.message });
        return;
      }
      setSubmission({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to classify',
      });
    }
  }, [
    classifyAsExpenditureAsync,
    classificationHint,
    expenditureCategory,
    expenditureContactId,
    expenditurePurpose,
    expenditureRefundOfContributionId,
    expenditureSubCategory,
    handleAfterMutation,
    receiptAttachable,
    row.concurrencyVersion,
    row.id,
    selectedCapturedReceiptId,
    setSubmission,
  ]);

  const handleClassify = useCallback(() => {
    if (selectedType === 'exclude') {
      void handleExcludeFromReport();
      return;
    }
    if (selectedType === 'contribution') {
      void handleClassifyContribution();
      return;
    }
    if (selectedType === 'expenditure') {
      void handleClassifyExpenditure();
      return;
    }
  }, [
    handleClassifyContribution,
    handleClassifyExpenditure,
    handleExcludeFromReport,
    selectedType,
  ]);

  const classifyHint: ClassifyHint | null = (() => {
    if (submission.kind === 'error') {
      return { type: 'neutral', messages: [submission.message] };
    }
    if (selectedType === 'contribution' && blockingGates.length > 0) {
      return { type: 'gate', messages: blockingGates.map((g) => g.message) };
    }
    return null;
  })();

  const canFooterClassify =
    canClassify
    && (selectedType === 'exclude'
      || selectedType === 'expenditure'
      || (selectedType === 'contribution' && !blockedByGates));

  const classifyLabel = (() => {
    if (selectedType === 'exclude') return 'Exclude from report';
    return 'Classify';
  })();

  return (
    // Two-column shell: a left context rail (what you read to decide) and a
    // right action column (what you do). `@container` lives on the OUTER panel,
    // making the panel a query container for its descendants — a container
    // element never queries its own width, only ancestors'. So the `@lg:flex-row`
    // two-column split lives one level down on the INNER wrapper, where it
    // resolves against the panel's own width: side-by-side at ≥32rem (the panel
    // mounts at ~672px in the modal and wider on a ledger row), stacked below.
    <div
      data-testid="bank-transaction-classification-panel"
      className="@container overflow-hidden rounded-lg border bg-card animate-in slide-in-from-right-4 motion-reduce:animate-none"
    >
      <div className="flex h-full flex-col @lg:flex-row">
        <div
          data-testid="classification-context-rail"
          className="shrink-0 border-b border-slate-200 bg-muted/30 p-4 @lg:w-52 @lg:border-b-0 @lg:border-r"
        >
          <PanelHeader
            id={row.id}
            date={row.date}
            amount={row.amount}
            description={row.description}
            sourceLabel={row.sourceLabel}
            onClose={onClose}
          />
        </div>

        <div
          data-testid="classification-action-column"
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-4"
        >
          {!hideTypeToggle && (
            <TypeToggle<BankTransactionClassificationType>
              options={typeOptions}
              selectedType={selectedType}
              onSelect={setSelectedType}
            />
          )}

          {selectedType === 'contribution' && (
            <ContributionPathBody
              form={form}
              contacts={contacts}
              prospects={prospects}
              onContactSearchChange={onContactSearchChange}
              settings={settings}
              profile={profile}
              rowAmount={row.amount}
              gateOutcomes={gateOutcomes}
              isClassifying={isClassifyingContribution}
              onClassifyWithOverrides={handleClassifyContribution}
              isCreatingContact={isCreatingContact}
              onQuickCreateSave={handleQuickCreateSave}
              suggestedContactId={row.suggestedContactId}
              isSavingContactAddress={isSavingContactAddress}
              onSaveContactAddress={handleSaveContactAddress}
            />
          )}
          {selectedType === 'expenditure' && (
            <ExpenditurePathBody
              form={form}
              payees={contacts}
              refundCandidates={refundCandidates}
              profile={profile}
              rowAmount={row.amount}
              rowDescription={row.description}
              bankTransactionId={row.id}
              onExcludeWithNote={handleExcludeWithNote}
              isExcluding={isExcluding}
              isCreatingContact={isCreatingContact}
              onQuickCreateSave={handleExpenditureQuickCreateSave}
              isSavingContactAddress={isSavingContactAddress}
              onSaveContactAddress={handleSaveContactAddress}
              receiptSuggestions={receiptSuggestions}
              selectedReceiptId={selectedCapturedReceiptId}
              onSelectReceipt={setSelectedCapturedReceiptId}
            />
          )}
          {selectedType === 'exclude' && (
            <div data-testid="exclude-body" className="mb-3 space-y-3">
              <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                Mark this bank line as not part of campaign reporting — personal, a
                transfer, or a mistake. No Contribution or Expenditure will be
                created, and it won&apos;t appear in any total or filing.
              </p>
              <div>
                <label
                  htmlFor="exclude-note"
                  className="mb-1 block text-sm font-medium text-foreground"
                >
                  Note (optional)
                </label>
                <textarea
                  id="exclude-note"
                  data-testid="exclude-note"
                  rows={2}
                  maxLength={FINANCE_MAX_BANK_TRANSACTION_EXCLUDE_NOTE_LENGTH}
                  value={excludeNote}
                  onChange={(e) => setExcludeNote(e.target.value)}
                  placeholder="e.g. personal expense, transfer between our accounts, or a duplicate"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </div>
          )}
          <ActionFooter
            isEditing={false}
            canClassify={canFooterClassify}
            isClassifying={
              submission.kind === 'submitting'
              || isClassifyingContribution
              || isClassifyingExpenditure
              || isExcluding
            }
            blockedByGates={selectedType === 'contribution' && blockedByGates}
            classifyHint={classifyHint}
            classifyLabel={classifyLabel}
            onCancel={onClose}
            onClassify={handleClassify}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * BSW-008: Contribution has no native ReferenceNumber column (the design
 * doc § 4 calls for one but adding the column would expand scope past
 * BSW-008's "Contribution path" boundary). Pragmatic stash: prepend the
 * reference number to the notes field with a stable prefix so the data
 * survives the persistence boundary. BSW-011 / BSW-012 may revisit if
 * structured access is needed downstream.
 */
function composeNotes(notes: string, referenceNumber: string): string | null {
  const trimmedRef = referenceNumber.trim();
  const trimmedNotes = notes.trim();
  if (!trimmedRef && !trimmedNotes) return null;
  if (!trimmedRef) return trimmedNotes;
  if (!trimmedNotes) return `Ref: ${trimmedRef}`;
  return `Ref: ${trimmedRef}\n${trimmedNotes}`;
}
